import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AREAS, CLASSIFICACOES, PESTEL, CHART_COLORS, labelOf } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid, Legend, LabelList,
} from "recharts";
import { useTheme } from "@/contexts/ThemeContext";
import { TrendingUp, FileCheck2, FileWarning, FileX2, Database, Globe, Activity, Building2,
         Sprout, Layers, FilePlus2, FileX, Archive, FileText, Link2, GitBranch,
         CheckCircle2, BadgeCheck, Brain, Target } from "lucide-react";
import KpiDrilldownModal from "@/components/KpiDrilldownModal";
import DashboardTooltip from "@/components/DashboardTooltip";
import ModuleHelp from "@/components/help/ModuleHelp";

// Nomes curtos para o gráfico de áreas
const SHORT_AREA = {
  GLO: "GLO",
  Fronteiras: "Fronteiras",
  SegurancaMaritima: "Seg. Marítima",
  InteligenciaDefesa: "Intel. Defesa",
  ApoioInteragencias: "Apoio Interagências",
  DefesaCivil: "Defesa Civil",
  GrandesEventos: "Grandes Eventos",
  Antiterrorismo: "Antiterrorismo",
  IlicitosAmbientais: "Ilícitos Ambientais",
};

const KpiCard = ({ label, value, sub, icon: Icon, accent, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`card-mil p-5 text-left w-full transition ${accent ? "gold-accent-top" : ""} ${onClick ? "cursor-pointer hover:border-[var(--gold)] hover:-translate-y-0.5 hover:shadow-md" : "cursor-default opacity-90"}`}
    data-testid={testid || `kpi-${String(label).toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="label-mil flex items-center gap-1">
          <span>{label}</span>
          <DashboardTooltip label={label} />
        </div>
        <div className="kpi-num mt-2">{value ?? "—"}</div>
        {sub && <div className="text-xs text-[var(--text-muted)] mt-2 font-mono">{sub}</div>}
      </div>
      {Icon && <Icon className="text-[var(--gold)] flex-shrink-0" size={20} strokeWidth={1.5} />}
    </div>
  </button>
);

const Panel = ({ title, hint, children, testid }) => (
  <div className="card-mil p-5" data-testid={testid}>
    <div className="flex items-baseline justify-between mb-4">
      <div>
        <h3 className="font-display font-bold text-[var(--text)] text-base">{title}</h3>
        {hint && <p className="text-xs text-[var(--text-muted)] mt-1">{hint}</p>}
      </div>
    </div>
    {children}
  </div>
);

const tooltipStyle = {
  backgroundColor: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 4,
  fontSize: 12,
  color: "var(--text)",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [drill, setDrill] = useState(null);
  const { theme } = useTheme();

  const tooltipStyle = {
    backgroundColor: theme === "light" ? "#ffffff" : "#0A1118",
    border: `1px solid ${theme === "light" ? "#d8dee6" : "#2A3A4A"}`,
    borderRadius: 4,
    fontSize: 12,
    color: theme === "light" ? "#0f1b2d" : "#f8fafc",
  };
  const axisColor = theme === "light" ? "#4b5b71" : "#8BA3B8";
  const gridStroke = theme === "light" ? "#e1e7ee" : "#2A3A4A";

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setData(r.data)).catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="text-[var(--danger)]" data-testid="dashboard-error">{error}</div>;
  if (!data) return <div className="font-mono text-sm text-[var(--text-muted)]" data-testid="dashboard-loading">CARREGANDO INDICADORES…</div>;

  const { totals, por_area, por_classificacao, por_pestel, por_recorte, por_pais, instituicoes, timeline, trends, metodologia_v2 } = data;
  const v2 = metodologia_v2 || {};

  // Drilldown handler — abre o modal genérico com config específica do card clicado
  const openDrill = (cfg) => setDrill(cfg);

  // Áreas com nome curto, ordenado desc
  const areaData = [...por_area]
    .map((d) => ({ short: SHORT_AREA[d.name] || d.name, full: labelOf(AREAS, d.name), value: d.value }))
    .sort((a, b) => b.value - a.value);
  const classData = por_classificacao.map((d) => ({ ...d, name: labelOf(CLASSIFICACOES, d.name) }));
  const pestelData = por_pestel.map((d) => ({ ...d, name: labelOf(PESTEL, d.name) }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="label-mil text-[var(--gold)]">Painel Executivo</div>
        <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-3">Painel · Visão Estratégica <ModuleHelp module="dashboard" /></h1>
        <p className="text-[var(--text-muted)] text-sm mt-2 max-w-3xl">
          Indicadores institucionais consolidados de coleta, validação e classificação de sementes prospectivas
          em Segurança Pública, Segurança Internacional e Defesa — horizonte ≥ 2030.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard label="Fontes" value={totals.sources} icon={Database} accent
          onClick={() => openDrill({ entity: "sources", title: "Todas as Fontes", subtitle: "Documentos catalogados no acervo institucional", icon: Database, filters: {} })} />
        <KpiCard label="Sementes" value={totals.seeds} icon={Activity} accent
          onClick={() => openDrill({ entity: "seeds", title: "Todas as Sementes de Futuro", subtitle: "Registros prospectivos em todas as fases de validação", icon: Sprout, filters: {} })} />
        <KpiCard label="Aprovadas" value={totals.aprovadas} icon={FileCheck2}
          onClick={() => openDrill({ entity: "seeds", title: "Sementes Aprovadas", subtitle: "Sementes validadas pelo coordenador/revisor", icon: FileCheck2, filters: { status: "aprovado" } })} />
        <KpiCard label="Pendentes" value={totals.pendentes} icon={FileWarning}
          onClick={() => openDrill({ entity: "seeds", title: "Sementes Pendentes de Revisão", subtitle: "Aguardando análise da Revisão Humana", icon: FileWarning, filters: { status: "pendente" } })} />
        <KpiCard label="Rejeitadas" value={totals.rejeitadas} icon={FileX2}
          onClick={() => openDrill({ entity: "seeds", title: "Sementes Rejeitadas", subtitle: "Sementes recusadas com justificativa", icon: FileX2, filters: { status: "rejeitado" } })} />
        <KpiCard label="Rascunhos" value={totals.rascunhos} icon={Database}
          onClick={() => openDrill({ entity: "seeds", title: "Sementes em Rascunho", subtitle: "Sementes ainda não enviadas para revisão", icon: Database, filters: { status: "rascunho" } })} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Últimos 7 dias" value={trends.d7} sub="novos registros" icon={TrendingUp}
          onClick={() => openDrill({ entity: "seeds", title: "Sementes dos últimos 7 dias", subtitle: "Cadastros recentes no acervo prospectivo", icon: TrendingUp, filters: { since_days: 7 } })} />
        <KpiCard label="Últimos 30 dias" value={trends.d30} sub="novos registros" icon={TrendingUp}
          onClick={() => openDrill({ entity: "seeds", title: "Sementes dos últimos 30 dias", subtitle: "Tendência mensal de coleta", icon: TrendingUp, filters: { since_days: 30 } })} />
        <KpiCard label="Últimos 90 dias" value={trends.d90} sub="novos registros" icon={TrendingUp}
          onClick={() => openDrill({ entity: "seeds", title: "Sementes dos últimos 90 dias", subtitle: "Recorte trimestral de produção institucional", icon: TrendingUp, filters: { since_days: 90 } })} />
      </div>

      {/* Indicadores Metodológicos V2 — Fonte → Insumo → Semente */}
      <div data-testid="v2-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="label-mil text-[var(--gold)]">Manual Metodológico V2</div>
            <h2 className="font-display text-xl font-bold mt-1">Pipeline Institucional · Fonte → Insumo → Semente</h2>
          </div>
          <div className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-widest">12 indicadores institucionais</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="v2-kpis">
          <KpiCard label="Insumos" value={v2.total_inputs} icon={Layers} accent
            onClick={() => openDrill({ entity: "inputs", title: "Todos os Insumos", subtitle: "Trechos identificados em fontes — etapa intermediária do pipeline V2", icon: Layers, filters: {}, sortBy: "ai_confidence_score" })} />
          <KpiCard label="Insumos em rascunho" value={v2.inputs_rascunho} icon={FilePlus2}
            onClick={() => openDrill({ entity: "inputs", title: "Insumos em Rascunho", subtitle: "Aguardando revisão / promoção a Semente", icon: FilePlus2, filters: { status: "rascunho" } })} />
          <KpiCard label="Insumos promovidos" value={v2.inputs_promovidos} icon={GitBranch}
            onClick={() => openDrill({ entity: "inputs", title: "Insumos Promovidos a Sementes", subtitle: "Trechos já convertidos em Sementes de Futuro", icon: GitBranch, filters: { status: "promovido" } })} />
          <KpiCard label="Insumos descartados" value={v2.inputs_descartados} icon={FileX}
            onClick={() => openDrill({ entity: "inputs", title: "Insumos Descartados", subtitle: "Trechos avaliados como não prospectivos", icon: FileX, filters: { status: "descartado" } })} />
          <KpiCard label="Sementes via V2" value={v2.seeds_via_v2} icon={Sprout} accent
            onClick={() => openDrill({ entity: "seeds", title: "Sementes geradas via Metodologia V2", subtitle: "Sementes nascidas do pipeline Fonte → Insumo → Semente", icon: Sprout, filters: { origem_cadastro: "insumo_v2" } })} />
          <KpiCard label="Score IA médio" value={v2.media_score_ia ? `${v2.media_score_ia}` : "—"} sub="confiança dos insumos" icon={Brain}
            onClick={() => openDrill({ entity: "inputs", title: "Insumos ordenados por confiança IA", subtitle: "Maior score de confiança Gemini primeiro", icon: Brain, filters: {}, sortBy: "ai_confidence_score" })} />
          <KpiCard label="Fontes c/ PDF anexo" value={v2.sources_com_pdf} icon={FileText} testid="kpi-fontes-pdf-anexo"
            onClick={() => openDrill({ entity: "sources", title: "Fontes com PDF anexado", subtitle: "Documentos arquivados localmente no LSC/EGN", icon: FileText, filters: { has_pdf: true } })} />
          <KpiCard label="Fontes c/ link Drive" value={v2.sources_link_drive} icon={Link2} testid="kpi-fontes-link-drive"
            onClick={() => openDrill({ entity: "sources", title: "Fontes com link Drive", subtitle: "Fontes vinculadas ao Drive institucional", icon: Link2, filters: { has_drive: true } })} />
          <KpiCard label="Sementes c/ Drive excepcional" value={v2.seeds_com_drive_excepcional} icon={Link2} testid="kpi-sementes-drive-excepcional"
            onClick={() => openDrill({ entity: "seeds", title: "Sementes com link Drive excepcional", subtitle: "Casos com link Drive registrado em caráter excepcional", icon: Link2, filters: { has_drive_excepcional: true } })} />
          <KpiCard label="Checklist 100%" value={v2.seeds_checklist_completo} sub="sementes operacionais" icon={CheckCircle2}
            onClick={() => openDrill({ entity: "seeds", title: "Sementes com Checklist Operacional 100%", subtitle: "Todos os 13 itens do Manual V2 marcados pelo pesquisador", icon: CheckCircle2, filters: { checklist_completo: true } })} />
          <KpiCard label="Sementes arquivadas" value={v2.seeds_arquivadas} icon={Archive}
            onClick={() => openDrill({ entity: "seeds", title: "Sementes Arquivadas", subtitle: "Sementes retiradas do fluxo ativo com observação administrativa", icon: Archive, filters: { status: "arquivado" } })} />
          <KpiCard label="Taxa de aprovação" value={`${v2.taxa_aprovacao ?? 0}%`} sub="sementes aprovadas/total" icon={Target} accent
            onClick={() => openDrill({ entity: "seeds", title: "Todas as Sementes — referência da taxa de aprovação", subtitle: `Taxa atual: ${v2.taxa_aprovacao ?? 0}% (aprovadas / total)`, icon: Target, filters: {} })} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Distribuição por Área Temática" testid="chart-area" hint="Áreas ordenadas pela frequência. Hover para nome completo.">
          <div className="lg:hidden">
            <ul className="space-y-2">
              {areaData.map((d) => {
                const max = areaData[0]?.value || 1;
                return (
                  <li key={d.short} className="text-sm">
                    <div className="flex items-center justify-between mb-0.5">
                      <span title={d.full}>{d.short}</span>
                      <span className="font-mono text-xs text-[var(--gold)]">{d.value}</span>
                    </div>
                    <div className="h-1.5 bg-[var(--surface-2)] rounded overflow-hidden">
                      <div className="h-full bg-[var(--gold)]" style={{ width: `${(d.value / max) * 100}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="hidden lg:block">
            <ResponsiveContainer width="100%" height={Math.max(320, areaData.length * 36)}>
              <BarChart data={areaData} layout="vertical" margin={{ left: 12, right: 36, top: 6, bottom: 6 }} barCategoryGap={10}>
                <CartesianGrid stroke={gridStroke} strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" stroke={axisColor} fontSize={11} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="short"
                  width={160}
                  stroke={axisColor}
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value, _name, item) => [value, item?.payload?.full || "Área"]}
                  cursor={{ fill: theme === "light" ? "rgba(176,136,32,0.08)" : "rgba(197,160,40,0.10)" }}
                />
                <Bar dataKey="value" fill="#C5A028" barSize={20} radius={[0, 3, 3, 0]}>
                  <LabelList dataKey="value" position="right" fill={theme === "light" ? "#0f1b2d" : "#f8fafc"} fontSize={12} fontWeight={600} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Classificação da Semente" testid="chart-classificacao">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={classData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {classData.map((entry) => (
                  <Cell key={entry.name} fill={CHART_COLORS[classData.indexOf(entry) % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 10, color: "#8BA3B8" }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Perspectiva PESTEL" testid="chart-pestel">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={pestelData}>
              <CartesianGrid stroke="#2A3A4A" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="name" stroke="#8BA3B8" fontSize={10} />
              <YAxis stroke="#8BA3B8" fontSize={10} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#38BDF8" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Linha do Tempo (90d)" testid="chart-timeline" hint="Cadastros por dia">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={timeline}>
              <CartesianGrid stroke="#2A3A4A" strokeDasharray="2 4" />
              <XAxis dataKey="date" stroke="#8BA3B8" fontSize={9} />
              <YAxis stroke="#8BA3B8" fontSize={10} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="value" stroke="#C5A028" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Países com mais publicações" testid="chart-paises">
          <ul className="space-y-2">
            {por_pais.slice(0, 8).map((p) => {
              const max = por_pais[0]?.value || 1;
              return (
                <li key={p.name} className="flex items-center gap-3 text-sm">
                  <Globe size={14} className="text-[var(--info)]" />
                  <span className="flex-1 truncate text-[var(--text)]">{p.name}</span>
                  <div className="w-28 h-2 bg-[var(--surface-2)] rounded overflow-hidden">
                    <div className="h-full bg-[var(--gold)]" style={{ width: `${(p.value / max) * 100}%` }} />
                  </div>
                  <span className="font-mono text-xs text-[var(--text-muted)] w-6 text-right">{p.value}</span>
                </li>
              );
            })}
          </ul>
        </Panel>

        <Panel title="Instituições mais recorrentes" testid="chart-instituicoes">
          <ul className="space-y-2">
            {instituicoes.slice(0, 8).map((i) => (
              <li key={i.name} className="flex items-center gap-3 text-sm">
                <Building2 size={14} className="text-[var(--gold)]" />
                <span className="flex-1 truncate text-[var(--text)]">{i.name}</span>
                <span className="badge badge-gold">{i.value}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Recorte Geográfico" testid="chart-recorte">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={por_recorte}>
              <CartesianGrid stroke="#2A3A4A" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="name" stroke="#8BA3B8" fontSize={10} />
              <YAxis stroke="#8BA3B8" fontSize={10} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#818CF8" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Tendências Emergentes" testid="panel-tendencias" hint="Sinais com horizonte ≥ 2030">
          <ul className="divide-y divide-[var(--border)]">
            {por_classificacao.slice(0, 5).map((c) => (
              <li key={c.name} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-display font-semibold text-sm">{labelOf(CLASSIFICACOES, c.name)}</div>
                  <div className="label-mil mt-0.5">Frequência consolidada</div>
                </div>
                <div className="kpi-num text-2xl">{c.value}</div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <KpiDrilldownModal config={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
